// Dependency imports
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';
import { useDispatch } from 'react-redux';

// Local imports
import { setFrameIndex } from 'store/annotations/annotationSlice';

// Style imports
import Styles from './AnnotationStyles.module.scss';
import {
  useGetConfigurationLabels,
  useGetFrames,
  useUpdateFrameData
} from 'api/sdk';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

// CSS for custom tooltip
const cssTooltipStyles = `
.frame-tooltip-container {
  position: relative;
  display: inline-block;
}

.frame-tooltip {
  visibility: hidden;
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translate(-100%, -50%);
  background-color: black;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 1000;
  pointer-events: none;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.frame-tooltip::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 100%;
  margin-top: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent transparent black;
}

.frame-tooltip-container:hover .frame-tooltip {
  visibility: visible;
  opacity: 1;
}

/* Hide tooltip when scrolling */
.scrolling .frame-tooltip {
  visibility: hidden !important;
  opacity: 0 !important;
}
`;

interface Frame {
  frame_path: string;
  frame_idx: number;
}

interface FrameColor {
  fill: string;
  stroke: string;
}

interface FrameData {
  id: string;
  type: string;
  color: FrameColor;
  label: string;
  points: Array<[number, number]>;
}

interface BoundingBoxData {
  frame_data: FrameData[]; // Changed to array since frame_data is an array
}

interface ImageComponentProp {
  frame: Frame;
  frameIndex: number;
  setLoading: Dispatch<SetStateAction<boolean>>;
  boundingBoxData: null | BoundingBoxData;
  currentCameraId: string;
  currentConfigId: string;
  isScrolling?: boolean;
}

function arePropsEqual(
  prevProps: ImageComponentProp,
  nextProps: ImageComponentProp
): boolean {
  const primitivePropsEqual =
    prevProps.frame.frame_idx === nextProps.frame.frame_idx &&
    prevProps.frameIndex === nextProps.frameIndex &&
    prevProps.currentCameraId === nextProps.currentCameraId &&
    prevProps.currentConfigId === nextProps.currentConfigId &&
    prevProps.isScrolling === nextProps.isScrolling;

  if (!primitivePropsEqual) return false;

  if (!prevProps.boundingBoxData && !nextProps.boundingBoxData) {
    return true;
  }

  if (!prevProps.boundingBoxData || !nextProps.boundingBoxData) {
    return false;
  }

  const boundingBoxEqual =
    JSON.stringify(prevProps.boundingBoxData) ===
    JSON.stringify(nextProps.boundingBoxData);

  return boundingBoxEqual;
}

const ImageComponent = ({
  frame,
  frameIndex,
  setLoading,
  boundingBoxData,
  currentCameraId,
  currentConfigId,
  isScrolling = false
}: ImageComponentProp) => {
  const imgRef = useRef(null);
  const dispatch = useDispatch();
  const annotationData: any = useSelector(
    (state: RootState) => state.annotation.annotationData
  );
  const { mutateAsync: updateFrameData } = useUpdateFrameData();
  const { refetch } = useGetConfigurationLabels(currentConfigId);
  const { refetch: getFrames }: any = useGetFrames(
    currentConfigId,
    currentCameraId
  );

  // Add CSS styles once
  useEffect(() => {
    const styleTag = document.createElement('style');
    if (!document.head.querySelector('[data-frame-tooltip-styles]')) {
      styleTag.innerHTML = cssTooltipStyles;
      styleTag.setAttribute('data-frame-tooltip-styles', 'true');
      document.head.appendChild(styleTag);
    }

    return () => {
      // Only remove if this component is the last one using it
      if (document.querySelectorAll('.frame-tooltip-container').length <= 1) {
        const styleElement = document.head.querySelector(
          '[data-frame-tooltip-styles]'
        );
        if (styleElement && styleElement.parentNode) {
          styleElement.parentNode.removeChild(styleElement);
        }
      }
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            img.src = `${process.env.REACT_APP_MINIO_URL}/${frame.frame_path}`;
            observer.unobserve(img);
          }
        });
      },
      { rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, [frame]);

  // Check if there's frame data
  const hasFrameData =
    boundingBoxData?.frame_data && boundingBoxData.frame_data.length > 0;

  const handleFrameChange = async () => {
    try {
      setLoading(true);
      console.log('update frame Data inside image component', {
        configId: currentConfigId,
        cameraId: currentCameraId,
        frameIdx: frameIndex,
        data: {
          bounding_box: {
            frame_data: annotationData
          }
        }
      });
      await updateFrameData({
        configId: currentConfigId,
        cameraId: currentCameraId,
        frameIdx: frameIndex,
        data: {
          bounding_box: {
            frame_data: annotationData
          }
        }
      });
      dispatch(setFrameIndex(frame.frame_idx));
      await getFrames();
      await refetch();
    } catch (error) {
      console.error('Error updating frame data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`frame-tooltip-container ${isScrolling ? 'scrolling' : ''}`}
      style={{ position: 'relative' }}
    >
      <div className={Styles.imageContainer} style={{ position: 'relative' }}>
        <img
          ref={imgRef}
          onClick={() => {
            handleFrameChange();
          }}
          onLoadStart={() => setLoading(true)}
          onLoad={() => setLoading(false)}
          className={Styles.frameimage}
          style={{
            border:
              frame.frame_idx === frameIndex ? '3px solid #102633' : 'none'
          }}
          alt="frame"
          loading="lazy"
        />
        {hasFrameData && (
          <div
            className={Styles.overlay}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '85%',
              backgroundColor: 'rgba(0, 255, 0, 0.2)', // Green overlay with 0.2 opacity
              pointerEvents: 'none' // This ensures the overlay doesn't interfere with clicking
            }}
          />
        )}
      </div>
      <div className="frame-tooltip">{frame.frame_idx}</div>
    </div>
  );
};

export default React.memo(ImageComponent, arePropsEqual);
